import "./UserProfile.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import userService from "../../services/user-service";
import foodstuffService from "../../services/foodstuff-service";
import { GlobalContext } from "../GlobalContext";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import NumericPagination from "../../components/NumericPagination/NumericPagination";
import { ColumnHeader, ColumnValue, DefaultTableFooter } from "../../components/DefaultTable/DefaultTable";
import DefaultUserIcon from "../../assets/imgs/default-user-icon.png";
import { formatToDayMonthYearDate, formatToDayMonthYearDateTime } from "../../utils/date-converter";
import { useIsMobile } from "../../hooks/useMediaQuery";

const GENDER_LABELS = {
  MALE: "Masculino",
  FEMALE: "Feminino",
  NON_BINARY: "Não binário",
};

const ROLE_LABELS = {
  ROLE_USER: "Usuário",
  ROLE_NUTRITIONIST: "Nutricionista",
  ROLE_ADMIN: "Admin",
};

const PHYSICAL_GOAL_LABELS = {
  LOSE_WEIGHT: "Emagrecer",
  GAIN_MUSCLE_MASS: "Ganhar massa muscular",
  MAINTAIN_WEIGHT: "Manter peso",
  IMPROVE_DIET: "Melhorar alimentação",
  INCREASE_ENERGY: "Aumentar energia",
};

const ACTIVITY_FACTOR_LABELS = {
  SEDENTARY: "Sedentário",
  LIGHTLY_ACTIVE: "Pouco ativo",
  MODERATELY_ACTIVE: "Moderadamente ativo",
  VERY_ACTIVE: "Muito ativo",
  EXTRA_ACTIVE: "Super ativo",
};

const STATUS_LABELS = {
  ACTIVE: "Ativo",
  INACTIVE: "Inativo",
};

const SUGGESTION_STATUS_LABELS = {
  CREATED: "Pendente",
  ACCEPTED: "Concluída",
  REJECTED: "Rejeitada",
};

const SUGGESTION_REASON_LABELS = {
  SUGGESTION_APPLIED: "Sugestão aplicada",
  ALREADY_EXISTS: "Já existe",
  GENERIC_OR_IMPRECISE: "Genérico ou impreciso",
  INSUFFICIENT_INFORMATION: "Informação insuficiente",
  OUT_OF_SCOPE: "Fora do escopo",
  DUPLICATED: "Duplicada",
  OTHER: "Outro",
};

const MEALS_PAGE_SIZE = 7;
const SUGGESTIONS_PAGE_SIZE = 7;

const mealsColumnsWidth = {
  DATE: 2.5,
  NAME: 3,
  CALORIES: 1.5,
  ITEMS: 1,
};

const suggestionsColumnsWidth = {
  DATE: 2,
  NAME: 2.5,
  BRAND: 2,
  STATUS: 1.5,
  REASON: 2,
};

const formatLabel = (labels, value) => {
  if (!value) {
    return "—";
  }
  return labels[value] || value;
};

const formatAccountDate = (value) => {
  if (!value) {
    return "—";
  }

  const datePart = value.includes("T") ? value.split("T")[0] : value;
  return formatToDayMonthYearDate(datePart);
};

const UserProfile = () => {
  const { id } = useParams();
  const isMobile = useIsMobile();
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const [user, setUser] = useState(null);
  const [streak, setStreak] = useState(null);
  const [mealsSummary, setMealsSummary] = useState(null);
  const [mealsResponse, setMealsResponse] = useState({});
  const [meals, setMeals] = useState([]);
  const [currentMealsPage, setCurrentMealsPage] = useState(1);
  const [suggestionsSummary, setSuggestionsSummary] = useState(null);
  const [suggestionsResponse, setSuggestionsResponse] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [currentSuggestionsPage, setCurrentSuggestionsPage] = useState(1);

  const fetchMeals = useCallback(
    async (page) => {
      if (page < 0) {
        return fetchMeals(0);
      }

      setIsLoading(true);
      setCurrentMealsPage(page + 1);
      try {
        const response = await userService.getUserMealsHistory(id, page, MEALS_PAGE_SIZE);
        setMealsResponse(response);
        setMeals(response.content || []);

        if (response.total_pages > 0 && page >= response.total_pages) {
          fetchMeals(response.total_pages - 1);
        }
      } catch (error) {
        console.log("Erro ao buscar refeições", error);
      } finally {
        setIsLoading(false);
      }
    },
    [id, setIsLoading]
  );

  const fetchSuggestions = useCallback(
    async (page) => {
      if (page < 0) {
        return fetchSuggestions(0);
      }

      setIsLoading(true);
      setCurrentSuggestionsPage(page + 1);
      try {
        const response = await foodstuffService.getFoodstuffSuggestionsByUser(
          id,
          page,
          SUGGESTIONS_PAGE_SIZE
        );
        setSuggestionsResponse(response);
        setSuggestions(response.content || []);

        if (response.total_pages > 0 && page >= response.total_pages) {
          fetchSuggestions(response.total_pages - 1);
        }
      } catch (error) {
        console.log("Erro ao buscar solicitações", error);
      } finally {
        setIsLoading(false);
      }
    },
    [id, setIsLoading]
  );

  const fetchProfileData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [userData, streakData, mealsSummaryData, suggestionsSummaryData] = await Promise.all([
        userService.getUserById(id),
        userService.getUserStreak(id),
        userService.getUserMealsHistorySummary(id),
        foodstuffService.getFoodstuffSuggestionsSummary(id),
      ]);

      setUser(userData);
      setStreak(streakData);
      setMealsSummary(mealsSummaryData);
      setSuggestionsSummary(suggestionsSummaryData);
    } catch (error) {
      console.log("Erro ao buscar perfil do usuário", error);
    } finally {
      setIsLoading(false);
    }
  }, [id, setIsLoading]);

  useEffect(() => {
    document.title = "Perfil do usuário";
    setSelectedScreen("user-profile");
    fetchProfileData();
    fetchMeals(0);
    fetchSuggestions(0);
  }, [id]);

  const activeMealPlan = user?.meal_plans?.find((mealPlan) => mealPlan.status === "ACTIVE");

  return (
    <div className="right-side">
      <div className="right-side__content user-profile">
        <Breadcrumb
          previousPage={{ href: "/users", title: "Usuários" }}
          currentPage={{ title: user?.name || "Perfil" }}
        />

        {user && (
          <>
            <section className="user-profile__header">
              <div className="user-profile__header__avatar">
                <img src={user.photo_url || DefaultUserIcon} alt="" />
              </div>
              <div className="user-profile__header__info">
                <h1>{user.name}</h1>
                <p className="user-profile__header__email">{user.email}</p>
                <div className="user-profile__badges">
                  <span className="user-profile__badge user-profile__badge--role">
                    {formatLabel(ROLE_LABELS, user.main_role)}
                  </span>
                  <span
                    className={`user-profile__badge user-profile__badge--${
                      user.account_status === "ACTIVE" ? "active" : "inactive"
                    }`}
                  >
                    Conta: {formatLabel(STATUS_LABELS, user.account_status)}
                  </span>
                  <span
                    className={`user-profile__badge user-profile__badge--${
                      user.activity_status === "ACTIVE" ? "active" : "inactive"
                    }`}
                  >
                    Atividade 7d: {formatLabel(STATUS_LABELS, user.activity_status)}
                  </span>
                  <span className="user-profile__badge user-profile__badge--streak">
                    Streak: {streak?.current ?? user.active_days ?? 0} dias
                  </span>
                </div>
                <p className="user-profile__meta">
                  Cadastro em {formatAccountDate(user.created_at)}
                </p>
              </div>
            </section>

            <div className="user-profile__grid">
              <section className="user-profile__card">
                <h2>Perfil nutricional</h2>
                <dl className="user-profile__details">
                  <div>
                    <dt>Gênero</dt>
                    <dd>{formatLabel(GENDER_LABELS, user.gender)}</dd>
                  </div>
                  <div>
                    <dt>Data de nascimento</dt>
                    <dd>
                      {user.birth_date ? formatToDayMonthYearDate(user.birth_date) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Peso</dt>
                    <dd>
                      {user.body_measurement?.weight
                        ? `${user.body_measurement.weight} kg`
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Altura</dt>
                    <dd>
                      {user.body_measurement?.height
                        ? `${user.body_measurement.height} cm`
                        : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt>Objetivo</dt>
                    <dd>{formatLabel(PHYSICAL_GOAL_LABELS, user.physical_goal)}</dd>
                  </div>
                  <div>
                    <dt>Nível de atividade</dt>
                    <dd>
                      {formatLabel(ACTIVITY_FACTOR_LABELS, user.physical_activity_factor)}
                    </dd>
                  </div>
                </dl>
              </section>

              <section className="user-profile__card">
                <h2>Nutricionista</h2>
                <dl className="user-profile__details">
                  <div>
                    <dt>Vinculado</dt>
                    <dd>{user.nutritionist_name || "Sem nutricionista"}</dd>
                  </div>
                  <div>
                    <dt>Plano alimentar ativo</dt>
                    <dd>{activeMealPlan ? "Sim" : "Não"}</dd>
                  </div>
                  {activeMealPlan && (
                    <div>
                      <dt>Período do plano</dt>
                      <dd>
                        {formatToDayMonthYearDate(activeMealPlan.start_date)}
                        {activeMealPlan.end_date
                          ? ` — ${formatToDayMonthYearDate(activeMealPlan.end_date)}`
                          : " — em andamento"}
                      </dd>
                    </div>
                  )}
                </dl>
              </section>
            </div>

            <section className="user-profile__section">
              <h2>Refeições registradas</h2>
              <div className="user-profile__summary-cards">
                <div className="user-profile__summary-card">
                  <span className="user-profile__summary-card__value">
                    {mealsSummary?.total ?? 0}
                  </span>
                  <span className="user-profile__summary-card__label">Total</span>
                </div>
                <div className="user-profile__summary-card">
                  <span className="user-profile__summary-card__value">
                    {mealsSummary?.meals_last_30_days ?? 0}
                  </span>
                  <span className="user-profile__summary-card__label">Últimos 30 dias</span>
                </div>
                <div className="user-profile__summary-card">
                  <span className="user-profile__summary-card__value user-profile__summary-card__value--text">
                    {mealsSummary?.last_meal_date
                      ? formatToDayMonthYearDate(mealsSummary.last_meal_date)
                      : "—"}
                  </span>
                  <span className="user-profile__summary-card__label">Última refeição</span>
                </div>
              </div>

              {mealsResponse?.total_elements > 0 ? (
                <div className="default-table user-profile__table">
                  <div>
                    <div className="table__header user-profile__table__header--desktop">
                      <ColumnHeader title="Data/Hora" column="DATE" columnsWidth={mealsColumnsWidth} />
                      <ColumnHeader title="Refeição" column="NAME" columnsWidth={mealsColumnsWidth} />
                      <ColumnHeader title="Kcal" column="CALORIES" columnsWidth={mealsColumnsWidth} />
                      <ColumnHeader title="Itens" column="ITEMS" columnsWidth={mealsColumnsWidth} />
                    </div>
                    <div className="table__content">
                      {meals.map((meal) => (
                        <div className="table__row user-profile__table__row" key={meal.id}>
                          <ColumnValue column="DATE" columnsWidth={mealsColumnsWidth}>
                            {meal.date_time
                              ? formatToDayMonthYearDateTime(meal.date_time)
                              : "—"}
                          </ColumnValue>
                          <ColumnValue column="NAME" columnsWidth={mealsColumnsWidth}>
                            {meal.name || "—"}
                          </ColumnValue>
                          <ColumnValue column="CALORIES" columnsWidth={mealsColumnsWidth}>
                            {meal.total_of_calories ?? 0}
                          </ColumnValue>
                          <ColumnValue column="ITEMS" columnsWidth={mealsColumnsWidth}>
                            {meal.foodstuffs?.length ?? 0}
                          </ColumnValue>
                        </div>
                      ))}
                    </div>
                  </div>
                  <DefaultTableFooter>
                    <NumericPagination
                      currentPage={currentMealsPage || 1}
                      totalPages={mealsResponse.total_pages}
                      pagesPerView={isMobile ? 5 : 10}
                      onSelectPage={(page) => fetchMeals(page)}
                    />
                  </DefaultTableFooter>
                </div>
              ) : (
                <div className="user-profile__empty">
                  <p>Nenhuma refeição registrada.</p>
                </div>
              )}
            </section>

            <section className="user-profile__section user-profile__section--suggestions">
              <h2>Solicitações de alimentos</h2>
              <div className="user-profile__summary-cards">
                <div className="user-profile__summary-card">
                  <span className="user-profile__summary-card__value">
                    {suggestionsSummary?.created ?? 0}
                  </span>
                  <span className="user-profile__summary-card__label">Pendentes</span>
                </div>
                <div className="user-profile__summary-card">
                  <span className="user-profile__summary-card__value">
                    {suggestionsSummary?.accepted ?? 0}
                  </span>
                  <span className="user-profile__summary-card__label">Concluídas</span>
                </div>
                <div className="user-profile__summary-card">
                  <span className="user-profile__summary-card__value">
                    {suggestionsSummary?.rejected ?? 0}
                  </span>
                  <span className="user-profile__summary-card__label">Rejeitadas</span>
                </div>
              </div>

              {suggestionsResponse?.total_elements > 0 ? (
                <div className="default-table user-profile__table">
                  <div>
                    <div className="table__header user-profile__table__header--desktop">
                      <ColumnHeader title="Data" column="DATE" columnsWidth={suggestionsColumnsWidth} />
                      <ColumnHeader title="Alimento" column="NAME" columnsWidth={suggestionsColumnsWidth} />
                      <ColumnHeader title="Marca" column="BRAND" columnsWidth={suggestionsColumnsWidth} />
                      <ColumnHeader title="Status" column="STATUS" columnsWidth={suggestionsColumnsWidth} />
                      <ColumnHeader title="Motivo" column="REASON" columnsWidth={suggestionsColumnsWidth} />
                    </div>
                    <div className="table__content">
                      {suggestions.map((suggestion) => (
                        <div className="table__row user-profile__table__row" key={suggestion.id}>
                          <ColumnValue column="DATE" columnsWidth={suggestionsColumnsWidth}>
                            {suggestion.created_at
                              ? formatToDayMonthYearDateTime(suggestion.created_at)
                              : "—"}
                          </ColumnValue>
                          <ColumnValue column="NAME" columnsWidth={suggestionsColumnsWidth}>
                            {suggestion.foodstuff_name || "—"}
                          </ColumnValue>
                          <ColumnValue column="BRAND" columnsWidth={suggestionsColumnsWidth}>
                            {suggestion.brand_name || "—"}
                          </ColumnValue>
                          <ColumnValue column="STATUS" columnsWidth={suggestionsColumnsWidth}>
                            {formatLabel(SUGGESTION_STATUS_LABELS, suggestion.status)}
                          </ColumnValue>
                          <ColumnValue column="REASON" columnsWidth={suggestionsColumnsWidth}>
                            {formatLabel(SUGGESTION_REASON_LABELS, suggestion.reason)}
                          </ColumnValue>
                        </div>
                      ))}
                    </div>
                  </div>
                  <DefaultTableFooter>
                    <NumericPagination
                      currentPage={currentSuggestionsPage || 1}
                      totalPages={suggestionsResponse.total_pages}
                      pagesPerView={isMobile ? 5 : 10}
                      onSelectPage={(page) => fetchSuggestions(page)}
                    />
                  </DefaultTableFooter>
                </div>
              ) : (
                <div className="user-profile__empty">
                  <p>Nenhuma solicitação de alimento.</p>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
